import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { withValueCap } from 'pages/globals/global';
import { NumericFormat } from 'react-number-format';
import { useState, useEffect, useRef } from 'react';
import InputLabel from 'components/InputLabel/index';
import AddIconButton from 'components/button/AddIconButton';
import { useLocation, useNavigate } from 'react-router-dom';
import editIcon from 'assets/images/table-icons/edit_green.svg';
import DeleteIconButton from 'components/button/DeleteIconButton';
import SectionCard from 'components/section/SectionCard';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import ComptrollerConfirmModal from 'pages/comptroller/ComptrollerConfirmModal';
import { App, Col, DatePicker, Form, Input, Row, Select, Button, Tooltip } from 'antd';
import constantFormValues from '../../lease-record-management-v9-new-version/utils/formInputNames';
import { utcConverter, processArrays } from '../../lease-record-management-v9-new-version/utils/helperFunc';
import { Box, Table, Typography, useTheme, TableRow, TableBody, TableCell, TableHead, TableFooter, TableContainer } from '@mui/material';

const LeaseV9NewTrailModeDetailsForm = ({ handleSubmit, vendorList, screen = 'CREATE', viewData, showFooterActions = true }) => {
	const { message: messageApi } = App.useApp();
	const theme = useTheme();
	const navigate = useNavigate();
	const { state } = useLocation();
	const form = Form.useFormInstance();

	const grossRentList = Form.useWatch('grossRentList', form);
	const advanceAmountList = Form.useWatch('advanceAmountList', form);
	const currentLeaseTerm = Form.useWatch(constantFormValues?.leaseTerm?.key, form);
	const formAdvanceAmount = Form.useWatch(constantFormValues?.advanceAmount?.key, form);
	const currentPaymentFrequency = Form.useWatch(constantFormValues?.paymentFrequency?.key, form);

	const { maxRecordedDate } = useSelector((state) => state.lease);

	const grossRentKey = useRef('');
	const advanceAmountKey = useRef('');
	const isUpdatingRef = useRef(false);

	const LeaseEndDate = dayjs().add(1, 'month');

	const [rowTotals, setRowTotals] = useState([]);
	const [totalAmount, setTotalAmount] = useState(0);
	const [showGoAndSetSubmitModal, setShowGoAndSetSubmitModal] = useState(false);

	const [goAndSetAction] = useSendControlDataMutation();

	const handleLeaseDateChange = () => {
		const leaseStartDate = form.getFieldValue('leaseStartDate');

		if (!leaseStartDate) return;

		const leaseTerm = form.getFieldValue('leaseTerm');

		if (!leaseTerm) {
			form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));

			return;
		}

		const leaseEndDate = leaseStartDate.add(leaseTerm, 'month').subtract(1, 'day');

		form.setFieldValue('leaseEndDate', utcConverter(leaseEndDate));
		form.setFieldValue('leaseStartDate', utcConverter(leaseStartDate));
		form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], utcConverter(leaseStartDate));
	};

	const handlePaymentFrequencyChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		try {
			form.setFieldValue(['changes', 0, 'paymentFrequency'], value);
		} finally {
			isUpdatingRef.current = false;
		}

		return value;
	};

	const handlePaymentModalityChange = (value) => {
		if (isUpdatingRef.current) return value;

		isUpdatingRef.current = true;

		try {
			form.setFieldValue(['changes', 0, 'paymentTime'], value);
		} finally {
			isUpdatingRef.current = false;
		}
		return value;
	};

	const handleGrossRentAmountChange = () => {
		const timeoutId = setTimeout(() => {
			handleApplyGrossRent();
		}, 10);

		return () => {
			clearTimeout(timeoutId);
		};
	};

	const handleAdvanceAmountToChange = () => {
		setTimeout(handleApplyAdvanceAmount, 10);
	};

	const validateGrossRentToValueOnBlur = (index) => {
		const currentValues = form.getFieldValue('grossRentList');

		if (!currentValues) return;

		const toValue = parseInt(currentValues[index].to);
		const fromValue = parseInt(currentValues[index].from);

		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			messageApi.error("'To' value must be greater than or equal to 'From' value");
			form.setFields([
				{
					name: ['grossRentList', index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		setTimeout(handleApplyGrossRent, 10);
	};

	const isGrossRentRowValid = (row) => {
		if (!row) return false;

		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	const handleGrossRentAddRow = (index) => {
		const leaseType = form.getFieldValue('leaseType');
		const currentValues = form.getFieldValue('grossRentList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
			return;
		}

		const currentRow = currentValues[index];

		if (!isGrossRentRowValid(currentRow)) {
			messageApi.error('Please complete all required fields in the current row before adding a new one');
			return;
		}

		const toValue = parseInt(currentRow.to);

		if (leaseType === 'Operating Lease' && toValue >= 12) {
			messageApi.error('Maximum periods taken');
			return;
		}

		const newFrom = toValue + 1;

		const newRow = { from: newFrom, to: '', amount: '', remarks: '' };

		// Insert the new row after the current one
		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

		// Fix the From values for all subsequent rows
		for (let i = index + 2; i < updatedValues.length; i++) {
			const prevTo = parseInt(updatedValues[i - 1].to);
			if (!isNaN(prevTo)) {
				updatedValues[i].from = prevTo + 1;
			}
		}

		form.setFieldValue('grossRentList', updatedValues);

		// Automatically apply the changes
		setTimeout(handleApplyGrossRent, 10);
	};

	const handleApplyGrossRent = () => {
		const currentValues = form.getFieldValue('grossRentList');

		if (!currentValues || currentValues.length === 0) return;
		const transformedData = transformRangeDataToAmountArray(currentValues);

		form.setFieldValue('grossRents', transformedData);

		const currentAdvanceAmountsValues = form.getFieldValue('advanceAmountList');

		if (!currentAdvanceAmountsValues || currentAdvanceAmountsValues?.length === 0) return;

		const transformedDataAdvanceAmounts = transformRangeDataToAmountArray(currentAdvanceAmountsValues);

		const { processedAdvanceAmounts } = processArrays(transformedData, transformedDataAdvanceAmounts);

		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);
	};

	function transformRangeDataToAmountArray(rangeData) {
		// Initialize an empty result array
		const result = [];

		// Process each range object
		rangeData.forEach((range) => {
			const fromValue = parseInt(range.from);
			const toValue = parseInt(range.to);

			// Skip invalid ranges
			if (isNaN(fromValue) || isNaN(toValue) || fromValue > toValue) {
				console.warn('Invalid range found:', range);
				return;
			}

			// Convert amount string to number (remove commas)
			const amount = parseFloat(String(range.amount).replace(/,/g, ''));

			// Skip if amount is not a valid number
			if (isNaN(amount)) {
				console.warn('Invalid amount found:', range);
				return;
			}

			// Calculate how many times to repeat the amount ((to - from) + 1)
			const repetitions = toValue - fromValue + 1;

			// Add the amount to the result array the specified number of times
			for (let i = 0; i < repetitions; i++) {
				result.push(amount);
			}
		});

		return result;
	}

	const calculateRowTotals = () => {
		// if (!form || !formListName) return;

		try {
			// Get the current values from the form
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				const newRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					// If amount exists, convert it from string to number
					let amount = 0;
					if (item.amount) {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				setRowTotals(newRowTotals);
			}
		} catch (error) {
			console.error('Error calculating row totals:', error);
		}
	};

	const calculateTotal = () => {
		try {
			// Get the current values from the form
			const values = form.getFieldValue('advanceAmountList');

			if (values && Array.isArray(values)) {
				// Calculate row totals directly without waiting for state update
				const currentRowTotals = values.map((item) => {
					if (!item) return 0;

					const fromValue = parseInt(item.from);
					const toValue = parseInt(item.to);

					// If amount exists, convert it from string to number
					let amount = 0;
					if (item.amount !== undefined && item.amount !== null && item.amount !== '') {
						const amountStr = String(item.amount).replace(/,/g, '');
						amount = parseFloat(amountStr);
					}

					// Skip invalid values
					if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
						return 0;
					}

					// Calculate total: (to - from + 1) * amount
					return (toValue - fromValue + 1) * amount;
				});

				// Update row totals state
				setRowTotals(currentRowTotals);

				// Now calculate the grand total using the current row totals
				const sum = currentRowTotals.reduce((total, rowTotal) => total + (rowTotal || 0), 0);

				// Update total amount state
				setTotalAmount(sum);
			}
		} catch (error) {
			console.error('Error calculating total:', error);
		}
	};

	const handleAdvanceAmountChange = () => {
		setTimeout(() => {
			calculateTotal();
			handleApplyAdvanceAmount();
		}, 10);
	};

	const handleGrossRentChange = () => {
		setTimeout(() => {
			calculateRowTotals();
			handleApplyGrossRent();
		}, 10);
	};

	const validateAdvanceAmountToValueOnBlur = (index) => {
		const currentValues = form.getFieldValue('advanceAmountList');

		if (!currentValues) return;

		const toValue = parseInt(currentValues[index].to);
		const fromValue = parseInt(currentValues[index].from);

		if (!isNaN(toValue) && !isNaN(fromValue) && toValue < fromValue) {
			messageApi.error("'To' value must be greater than 'From' value");
			form.setFields([
				{
					name: ['advanceAmountList', index, 'to'],
					value: '',
					errors: ["'To' value must be greater than 'From' value"],
				},
			]);
		}

		calculateRowTotals();
		handleApplyAdvanceAmount();
	};

	const isRowValid = (row) => {
		if (!row) return false;

		// Check if all required fields are filled
		const toValue = parseInt(row.to);
		const fromValue = parseInt(row.from);

		if (isNaN(toValue) || toValue <= 0) return false;
		if (isNaN(fromValue) || fromValue <= 0) return false;
		if (toValue < fromValue) return false;
		if (!row.amount) return false;

		return true;
	};

	const handleAdvanceAmountAddRow = (index) => {
		const leaseType = form.getFieldValue('leaseType');
		const currentValues = form.getFieldValue('advanceAmountList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
			return;
		}

		const currentRow = currentValues[index];

		if (!isRowValid(currentRow)) {
			messageApi.error('Please complete all required fields in the current row before adding a new one');

			return;
		}

		const toValue = parseInt(currentRow.to);

		if (leaseType === 'Operating Lease' && toValue >= 12) {
			messageApi.error('Maximum periods taken');
			return;
		}

		const newFrom = toValue + 1;

		const newRow = { from: newFrom, to: '', amount: '', totalAmount: '' };

		const updatedValues = [...currentValues.slice(0, index + 1), newRow, ...currentValues.slice(index + 1)];

		for (let i = index + 2; i < updatedValues.length; i++) {
			const prevTo = parseInt(updatedValues[i - 1].to);
			if (!isNaN(prevTo)) {
				updatedValues[i].from = prevTo + 1;
			}
		}

		form.setFieldValue('advanceAmountList', updatedValues);

		setTimeout(handleApplyAdvanceAmount, 10);
	};

	const handleApplyAdvanceAmount = () => {
		const currentValues = form.getFieldValue('advanceAmountList');
		if (!currentValues || currentValues?.length === 0) return;

		const transformedData = transformRangeDataToAmountArray(currentValues);
		const grossRents = form.getFieldValue('grossRents');
		const { processedAdvanceAmounts } = processArrays(grossRents, transformedData);

		const total = transformedData.reduce((sum, amount) => sum + amount, 0);

		form.setFieldValue('advanceAmounts', processedAdvanceAmounts);

		setTotalAmount(total);

		form.setFieldsValue({
			advanceAmount: total,
			advanceAmounts: processedAdvanceAmounts,
		});

		form.validateFields(['advanceAmounts', 'advanceAmount']);
	};

	const handleInputMonthChange = (e, index) => {
		if (!e) {
			const currentChanges = form.getFieldValue('changes') || [];

			if (currentChanges[index]) {
				const updatedChanges = [...currentChanges];

				updatedChanges[index] = {
					...updatedChanges[index],
					inputChangeMonth: null,
					inputChangeEffectiveDate: null,
				};

				form.setFieldsValue({ changes: updatedChanges });
			}

			return;
		}

		const effectiveDate = dayjs(e);
		const leaseStart = dayjs(form.getFieldValue('leaseStartDate'));

		let monthDifference = (effectiveDate.year() - leaseStart.year()) * 12 + (effectiveDate.month() - leaseStart.month());
		monthDifference += 1;

		if (monthDifference < 0) {
			monthDifference = 0;
		}

		const currentChanges = form.getFieldValue('changes') || [];

		if (currentChanges[index]) {
			const updatedChanges = [...currentChanges];

			updatedChanges[index] = {
				...updatedChanges[index],
				inputChangeMonth: monthDifference,
				inputChangeEffectiveDate: utcConverter(effectiveDate),
			};

			form.setFieldsValue({ changes: updatedChanges });
		}
	};

	const getMaxDateForRow = (index) => {
		if (index === 0) {
			return null;
		}

		const leaseStartDate = dayjs(form.getFieldValue('leaseStartDate'));

		if (!leaseStartDate) return null;

		const changes = form.getFieldValue('changes');

		if (!changes || !changes[index - 1]) return null;

		const prevRowLeaseTerm = changes[index - 1].inputChangeLeaseTerm;

		if (!prevRowLeaseTerm) return null;

		return leaseStartDate.add(parseInt(prevRowLeaseTerm), 'month').subtract(1, 'day');
	};

	const handleGoAndSetButton = async () => {
		const payload = {
			data: viewData?._id,
			processCode: state?.processCode,
			scriptName: 'lease-controller-go-and-set',
		};

		try {
			await goAndSetAction(payload)?.then(() => navigate(-1, { state: state }));
		} catch (e) {
			console.error('Error in Go & Set action:', e);
		}
	};

	useEffect(() => {
		const currentPaymentFrequency = form.getFieldValue(constantFormValues?.paymentFrequency?.key);

		if (currentPaymentFrequency === 'Quarterly') {
			form.setFieldValue('modifiedLeaseTerm', Math.floor(form.getFieldValue('leaseTerm') / 3));
		} else {
			form.setFieldValue('modifiedLeaseTerm', form.getFieldValue('leaseTerm'));
		}
	}, [currentLeaseTerm, currentPaymentFrequency]);

	useEffect(() => {
		if (!Array.isArray(grossRentList) || !Array.isArray(advanceAmountList) || grossRentList.length === 0 || advanceAmountList.length === 0)
			return;

		const newGrossRentKey = grossRentList.map((item) => `${item.from}-${item.to}`).join('|');
		const newAdvanceAmountKey = advanceAmountList.map((item) => `${item.from}-${item.to}`).join('|');

		if (newGrossRentKey === grossRentKey.current && newAdvanceAmountKey === advanceAmountKey.current) return;

		if (isUpdatingRef.current) return;

		isUpdatingRef.current = true;

		try {
			grossRentKey.current = newGrossRentKey;
			advanceAmountKey.current = newAdvanceAmountKey;

			const maxGrossRentTerm = Math.max(...grossRentList.map((item) => item.to ?? 0));
			const maxAdvanceAmountTerm = Math.max(...advanceAmountList.map((item) => item.to ?? 0));

			const maxTerm = Math.max(maxGrossRentTerm, maxAdvanceAmountTerm);

			const currentLeaseTerm = form.getFieldValue('leaseTerm');

			if (currentLeaseTerm !== maxTerm) {
				form.setFieldValue('leaseTerm', maxTerm);

				const currentChangeLeaseTerm = form.getFieldValue(['changes', 0, 'inputChangeLeaseTerm']);

				if (currentChangeLeaseTerm !== maxTerm) {
					form.setFieldValue(['changes', 0, 'inputChangeLeaseTerm'], maxTerm);
				}
			}

			const leaseStartDate = form.getFieldValue('leaseStartDate');

			if (leaseStartDate && maxTerm) {
				const leaseEndDate = leaseStartDate.add(maxTerm, 'month').subtract(1, 'day');

				const currentLeaseEndDate = form.getFieldValue('leaseEndDate');

				if (!currentLeaseEndDate || !leaseEndDate.isSame(currentLeaseEndDate)) {
					form.setFieldValue('leaseEndDate', leaseEndDate);
				}
			}
		} finally {
			setTimeout(() => {
				isUpdatingRef.current = false;
			}, 0);
		}
	}, [grossRentList, advanceAmountList]);

	useEffect(() => {
		const currentValues = form.getFieldValue('grossRentList');
		if (!currentValues || !currentValues.length) {
			form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
		}

		handleApplyGrossRent();
	}, [form]);

	useEffect(() => {
		if (formAdvanceAmount !== undefined && formAdvanceAmount !== null) {
			if (formAdvanceAmount !== totalAmount) {
				setTotalAmount(formAdvanceAmount);
			}
		}
	}, [formAdvanceAmount]);

	useEffect(() => {
		if (totalAmount > 0) {
			const currentFormValue = form.getFieldValue(constantFormValues?.advanceAmount?.key);

			if (currentFormValue !== totalAmount) {
				form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);
			}
		}
	}, [totalAmount]);

	useEffect(() => {
		const currentValues = form.getFieldValue('advanceAmountList');

		if (!currentValues || !currentValues.length) {
			form.setFieldValue('advanceAmountList', [{ from: 1, to: '', amount: '', totalAmount: '' }]);
		}

		calculateTotal();
		calculateRowTotals();
	}, []);

	useEffect(() => {
		// Only update the form when the total actually changes
		if (totalAmount > 0) {
			form.setFieldValue(constantFormValues?.advanceAmount?.key, totalAmount);

			// Also update changes array if it exists
			const currentChanges = form.getFieldValue('changes');
			if (currentChanges && currentChanges.length > 0) {
				// Make a shallow copy to avoid direct state mutation
				const updatedChanges = [...currentChanges];
				updatedChanges[0] = {
					...updatedChanges[0],
					inputChangeAdvance: totalAmount,
				};
				form.setFieldValue('changes', updatedChanges);
			}

			// console.log('Synchronized advanceAmount with total:', totalAmount);
		}
	}, [totalAmount]);

	const sectionTitleSx = {
		color: '#344054',
		padding: '0 0 12px 0',
		fontWeight: 600,
		fontSize: '18px',
		borderBottom: '1px solid #eaeef4',
	};

	const detailSectionCardSx = {
		padding: '16px',
		borderRadius: '10px',
		border: '1px solid #e6ebf2',
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
			<SectionCard sx={detailSectionCardSx}>
				<Typography variant="h4" sx={sectionTitleSx}>
					Basic Information
				</Typography>

				<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
					<Form.Item noStyle name="tabIndex" />
					<Form.Item noStyle name="endOfFinancialYear" />
					<Form.Item noStyle name={constantFormValues?.version?.key} />
					<Form.Item name={constantFormValues?.leaseTerm?.key} noStyle />
					<Form.Item noStyle name={constantFormValues?.lessorName?.key} />
					<Form.Item noStyle name={constantFormValues?.paymentModalitylabel?.key} />

					<Col xs={24} md={8}>
						<Form.Item name={constantFormValues?.LeaseId?.key} label={constantFormValues?.LeaseId?.label}>
							<Input disabled placeholder="To be auto generated" />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item
							name={constantFormValues?.leaseTitle?.key}
							rules={constantFormValues?.leaseTitle?.rules}
							label={constantFormValues?.leaseTitle?.label}
						>
							<Input
								placeholder="Write here"
								disabled={screen === 'VIEW' ? true : false}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item
							name={constantFormValues?.lessorID?.key}
							rules={constantFormValues?.lessorID?.rules}
							label={constantFormValues?.lessorID?.label}
						>
							<Select
								showSearch
								filterOption={true}
								optionFilterProp="children"
								placeholder="Select from the list"
								disabled={screen === 'VIEW' ? true : false} style={{width: '100%'}}
								onSelect={(_, selectItem) => {
									form.setFieldValue(constantFormValues?.lessorName?.key, selectItem?.children);
									form.setFieldValue(constantFormValues?.lessorID?.key, _);
								}}
							>
								{vendorList?.map(({ label, value }) => (
									<Select.Option key={value} value={value}>
										{label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item
							name={constantFormValues?.leaseStartDate?.key}
							rules={constantFormValues?.leaseStartDate?.rules}
							label={constantFormValues?.leaseStartDate?.label}
							getValueFromEvent={(e) => {
								const value = e;

								form.setFieldValue(['changes', 0, 'inputChangeEffectiveDate'], value);

								return value;
							}}
						>
							<DatePicker
								format="YYYY-MM-DD"
								onChange={handleLeaseDateChange}
								disabled={screen === 'VIEW' ? true : false} style={{width: '100%'}}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item
							name={constantFormValues?.leaseEndDate?.key}
							rules={constantFormValues?.leaseEndDate?.rules}
							label={constantFormValues?.leaseEndDate?.label}
						>
							<DatePicker disabled value={LeaseEndDate} style={{width: '100%'}} />
						</Form.Item>
					</Col>

					<Col xs={24} md={8}>
						<Form.Item name="modifiedLeaseTerm" rules={constantFormValues?.leaseTerm?.rules} label={constantFormValues?.leaseTerm?.label}>
							<Input type="number" disabled placeholder="Write here" />
						</Form.Item>
					</Col>

					<Form.Item noStyle name={constantFormValues?.paymentModality?.key} />

					<Col xs={24} md={8}>
						<Form.Item
							name={constantFormValues?.leaseType?.key}
							rules={constantFormValues?.leaseType?.rules}
							label={constantFormValues?.leaseType?.label}
						>
							<Select
								showSearch
								filterOption={true}
								optionFilterProp="children"
								placeholder="Select from the list"
								disabled={screen === 'VIEW' ? true : false} style={{width: '100%'}}
								onSelect={(value) => {
									if (value === 'Operating Lease') {
										form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], 0);
										form.setFieldValue(constantFormValues?.initialDiscountRate?.key, 0);

										form.setFieldValue('grossRentList', [{ from: 1, to: '', amount: '', remarks: '' }]);
										form.setFieldValue('advanceAmountList', [
											{
												from: 1,
												to: '',
												amount: '',
												totalAmount: '',
											},
										]);
									}
								}}
							>
								{constantFormValues?.leaseType?.options?.map(({ key, value, label }) => (
									<Select.Option key={key} value={value}>
										{label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>
					</Col>

					<Col xs={12}>
						<Form.Item
							shouldUpdate={(previousValue, currentValue) =>
								previousValue?.[constantFormValues?.leaseType?.key] !== currentValue?.[constantFormValues?.leaseType?.key]
							}
							noStyle
						>
							{({ getFieldValue }) => {
								if (getFieldValue(constantFormValues?.leaseType?.key) === 'Finance Lease') {
									return (
										<Row gutter={[16, 0]}>
											<Col xs={24} md={16}>
												<Form.Item
													name={constantFormValues?.initialDiscountRate?.key}
													rules={constantFormValues?.initialDiscountRate?.rules}
													label={constantFormValues?.initialDiscountRate?.label}
													getValueFromEvent={(e) => {
														const value = e.target.value;

														form.setFieldValue(['changes', 0, 'inputChangeDiscountRate'], value);

														return value;
													}}
												>
													<Input
														placeholder="Write here"
														disabled={screen === 'VIEW' ? true : false}
													/>
												</Form.Item>
											</Col>
										</Row>
									);
								} else {
									return <Form.Item name={constantFormValues?.initialDiscountRate?.key} noStyle />;
								}
							}}
						</Form.Item>
					</Col>
				</Row>
			</SectionCard>

			<Row gutter={[24, 24]}>
				<Col xs={24} md={12}>
					<SectionCard sx={{ ...detailSectionCardSx, height: '100%' }}>
						<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
							Lease Payment Schedule
						</Typography>
						<>
							<Form.List name={'grossRentList'}>
								{(fields) => (
									<>
										<TableContainer
											className="gross-rent-table"
											sx={{
												'& table, & th, & td': {
													border: '1px solid #e0e0e0',
													borderCollapse: 'collapse',
												},
												'& th, & td': {
													p: '5px',
												},
												'& td .ant-form-item': {
													marginBottom: '0px',
												},

												'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
												'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
												'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
												'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
												'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
												'& table': { width: '100%' },
											}}
										>
											<Table aria-label="Gross Rent table">
												<TableHead>
													<TableRow>
														<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
															From Month
														</TableCell>

														<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
															To Month
														</TableCell>

														<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
															Amount
														</TableCell>

														{screen !== 'VIEW' && (
															<TableCell sx={{ textAlign: 'center', fontSize: '13px', backgroundColor: '#f6f5f5' }}>
																Action
															</TableCell>
														)}
													</TableRow>
												</TableHead>
												<TableBody>
													{fields.length > 0 ? (
														fields.map((field, index) => (
															<TableRow key={field.key}>
																<TableCell>
																	<Form.Item name={[field.name, 'from']}>
																		<Input
																			disabled={true} style={{width: '100%',
																				backgroundColor: '#f5f5f5',
																				textAlign: 'center'}}
																		/>
																	</Form.Item>
																</TableCell>

																<TableCell>
																	<Form.Item
																		name={[field.name, 'to']}
																		rules={[
																			{ required: true, message: "Please enter a 'To' value" },
																			({ getFieldValue }) => ({
																				validator(_, value) {
																					const leaseType = getFieldValue('leaseType');
																					const fromValue = getFieldValue([
																						'grossRentList',
																						field.name,
																						'from',
																					]);
																					if (!value || !fromValue) return Promise.resolve();
																					if (parseInt(value) < parseInt(fromValue)) {
																						return Promise.reject(
																							new Error("'To' must be greater than 'From'")
																						);
																					}
																					if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
																						return Promise.reject(new Error('Maximum month is 12'));
																					}
																					return Promise.resolve();
																				},
																			}),
																		]}
																	>
																		<Input
																			type="number"
																			placeholder="Enter To value"
																			disabled={screen === 'VIEW' ? true : false} style={{width: '100%',

																				textAlign: 'center'}}
																			onChange={() =>
																				handleGrossRentChange(`${'grossRentList'}.${field.name}.to`)
																			}
																			onBlur={() => validateGrossRentToValueOnBlur(index)}
																		/>
																	</Form.Item>
																</TableCell>

																<TableCell>
																	<Form.Item
																		name={[field.name, 'amount']}
																		rules={[{ required: true, message: 'Please enter an amount' }]}
																	>
																		<NumericFormat
																			decimalScale={2}
																			placeholder="0.00"
																			fixedDecimalScale
																			customInput={Input}
																			thousandSeparator=","
																			isAllowed={withValueCap}
																			disabled={screen === 'VIEW' ? true : false}
																			onValueChange={(values) => {
																				handleGrossRentAmountChange(
																					`${'grossRentList'}.${field.name}.amount`
																				);
																				const currentValues = [...form.getFieldValue('grossRentList')];

																				if (currentValues[index]) {
																					currentValues[index].amount = values.value || '';

																					form.setFieldValue('grossRentList', currentValues);
																				}
																			}}
																			onBlur={() => setTimeout(handleApplyGrossRent, 10)}
																		/>
																	</Form.Item>
																</TableCell>

																{screen !== 'VIEW' && (
																	<TableCell align="center">
																		<>
																			{fields.length === 1 && index === 0 && (
																				<Tooltip title="Add Row">
																					<AddIconButton onClick={() => handleGrossRentAddRow(index)} />
																				</Tooltip>
																			)}

																			{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																				<Tooltip title="Delete Row">
																					<DeleteIconButton
																						onClick={() => {
																							const currentValues = [
																								...form.getFieldValue('grossRentList'),
																							];

																							currentValues.splice(index, 1);

																							for (let i = index; i < currentValues.length; i++) {
																								if (i > 0) {
																									const prevTo = parseInt(currentValues[i - 1].to);
																									if (!isNaN(prevTo)) {
																										currentValues[i].from = prevTo + 1;
																									}
																								}
																							}

																							form.setFieldValue('grossRentList', currentValues);

																							setTimeout(handleApplyGrossRent, 10);
																						}}
																					/>
																				</Tooltip>
																			)}

																			{fields.length > 1 && index === fields.length - 1 && (
																				<>
																					<Tooltip title="Add Row">
																						<AddIconButton onClick={() => handleGrossRentAddRow(index)} />
																					</Tooltip>

																					<Tooltip title="Delete Row">
																						<DeleteIconButton
																							onClick={() => {
																								const currentValues = [
																									...form.getFieldValue('grossRentList'),
																								];

																								currentValues.splice(index, 1);

																								for (let i = index; i < currentValues.length; i++) {
																									if (i > 0) {
																										const prevTo = parseInt(
																											currentValues[i - 1].to
																										);
																										if (!isNaN(prevTo)) {
																											currentValues[i].from = prevTo + 1;
																										}
																									}
																								}

																								form.setFieldValue('grossRentList', currentValues);

																								setTimeout(handleApplyGrossRent, 10);
																							}}
																						/>
																					</Tooltip>
																				</>
																			)}

																			{fields.length > 1 && index === 0 && <span></span>}
																		</>
																	</TableCell>
																)}
															</TableRow>
														))
													) : (
														<TableRow>
															<TableCell colSpan={5} style={{ textAlign: 'center' }}>
																No data. Click plus icon in action column to add new entries.
															</TableCell>
														</TableRow>
													)}
												</TableBody>
											</Table>
										</TableContainer>

										<Form.Item name="grossRents" noStyle />
									</>
								)}
							</Form.List>
						</>

						<Form.Item name="identityId" noStyle />
						<Form.Item name="grossRentListSet" noStyle />
						<Form.Item name="effectiveDateHistory" noStyle />
						<Form.Item name="modificationSequence" noStyle />

						<Row gutter={[16, 0]} style={{ marginTop: '16px' }}>
							<Col xs={24} md={12}>
								<Form.Item
									name={constantFormValues?.paymentFrequency?.key}
									rules={constantFormValues?.paymentFrequency?.rules}
									getValueFromEvent={handlePaymentFrequencyChange}
									label={constantFormValues?.paymentFrequency?.label}
								>
									<Select
										showSearch
										filterOption={true}
										optionFilterProp="children"
										placeholder="Select from the list"
										disabled={screen === 'VIEW' ? true : false} style={{width: '100%'}}
									>
										{constantFormValues?.paymentFrequency?.options?.map(({ key, value, label }) => (
											<Select.Option key={key} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>

							<Col xs={24} md={12}>
								<Form.Item
									name={constantFormValues?.paymentModality?.key}
									rules={constantFormValues?.paymentModality?.rules}
									getValueFromEvent={handlePaymentModalityChange}
									label={constantFormValues?.paymentModality?.label}
								>
									<Select
										showSearch
										filterOption={true}
										optionFilterProp="children"
										placeholder="Select from the list"
										disabled={screen === 'VIEW' ? true : false} style={{width: '100%'}}
									>
										{constantFormValues?.paymentModality?.options?.map(({ key, value, label }) => (
											<Select.Option key={key} value={value}>
												{label}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
							</Col>
						</Row>
					</SectionCard>
				</Col>

				<Col xs={24} md={12}>
					<SectionCard sx={{ ...detailSectionCardSx, height: '100%' }}>
						<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
							Advance Payment & Adjustment
						</Typography>
						<div>
							<Form.List name={'advanceAmountList'}>
								{(fields) => {
									return (
										<>
											<TableContainer
												className="gross-rent-table"
												sx={{
													'& table, & th, & td': {
														border: '1px solid #e0e0e0',
														borderCollapse: 'collapse',
													},
													'& th, & td': {
														p: '5px',
													},
													'& td .ant-form-item': {
														marginBottom: '0px',
													},

													'& th:nth-of-type(1), & td:nth-of-type(1)': { width: '20%' },
													'& th:nth-of-type(2), & td:nth-of-type(2)': { width: '20%' },
													'& th:nth-of-type(3), & td:nth-of-type(3)': { width: '20%' },
													'& th:nth-of-type(4), & td:nth-of-type(4)': { width: '20%' },
													'& th:nth-of-type(5), & td:nth-of-type(5)': { width: '20%' },
													'& table': { width: '100%' },
												}}
											>
												<Table aria-label="Gross Rent table">
													<TableHead>
														<TableRow>
															<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
																From Month
															</TableCell>

															<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
																To Month
															</TableCell>

															<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
																Monthly Adjustment
															</TableCell>

															{screen !== 'VIEW' && (
																<TableCell sx={{ fontSize: '13px', backgroundColor: '#f6f5f5', textAlign: 'center' }}>
																	Action
																</TableCell>
															)}
														</TableRow>
													</TableHead>

													<TableBody>
														{fields.length > 0 ? (
															fields.map((field, index) => (
																<TableRow key={field.key}>
																	<TableCell>
																		<Form.Item name={[field.name, 'from']}>
																			<Input
																				disabled={true} style={{width: '100%',
																					textAlign: 'center',
																					backgroundColor: '#f5f5f5'}}
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'to']}
																			rules={[
																				{ required: false, message: "Please enter a 'To' value" },
																				({ getFieldValue }) => ({
																					validator(_, value) {
																						const leaseType = getFieldValue('leaseType');
																						const fromValue = getFieldValue([
																							'advanceAmountList',
																							field.name,
																							'from',
																						]);
																						if (!value || !fromValue) return Promise.resolve();
																						if (parseInt(value) < parseInt(fromValue)) {
																							return Promise.reject(
																								new Error("'To' must be greater than 'From'")
																							);
																						}
																						if (leaseType === 'Operating Lease' && parseInt(value) > 12) {
																							return Promise.reject(new Error('Maximum month is 12'));
																						}
																						return Promise.resolve();
																					},
																				}),
																			]}
																		>
																			<Input
																				type="number"
																				placeholder="Enter To value"
																				disabled={screen === 'VIEW' ? true : false} style={{width: '100%',

																					textAlign: 'center'}}
																				onChange={() =>
																					handleAdvanceAmountToChange(
																						`${'advanceAmountList'}.${field.name}.to`
																					)
																				}
																				onBlur={() => validateAdvanceAmountToValueOnBlur(index)}
																			/>
																		</Form.Item>
																	</TableCell>

																	<TableCell>
																		<Form.Item
																			name={[field.name, 'amount']}
																			rules={[{ required: false, message: 'Please enter an amount' }]}
																		>
																			<NumericFormat
																				decimalScale={2}
																				placeholder="0.00"
																				fixedDecimalScale
																				thousandSeparator=","
																				isAllowed={withValueCap}
																				disabled={screen === 'VIEW' ? true : false}
																				customInput={Input}
																				onValueChange={(values) => {
																					handleAdvanceAmountChange(
																						`${'advanceAmountList'}.${field.name}.amount`
																					);
																					const currentValues = [
																						...form.getFieldValue('advanceAmountList'),
																					];

																					if (currentValues[index]) {
																						currentValues[index].amount = values.value || '';

																						form.setFieldValue('advanceAmountList', currentValues);

																						const fromValue = parseInt(currentValues[index].from);
																						const toValue = parseInt(currentValues[index].to);

																						if (!values.value) {
																							const newRowTotals = [...rowTotals];
																							newRowTotals[index] = 0;
																							setRowTotals(newRowTotals);

																							const newTotal = newRowTotals.reduce(
																								(total, rowTotal) => total + (rowTotal || 0),
																								0
																							);

																							setTotalAmount(newTotal);
																							return;
																						}

																						const amount = parseFloat(values.value);

																						if (
																							!isNaN(fromValue) &&
																							!isNaN(toValue) &&
																							!isNaN(amount) &&
																							toValue >= fromValue
																						) {
																							const newRowTotals = [...rowTotals];
																							newRowTotals[index] = (toValue - fromValue + 1) * amount;
																							setRowTotals(newRowTotals);

																							const newTotal = newRowTotals.reduce(
																								(total, rowTotal) => total + (rowTotal || 0),
																								0
																							);
																							setTotalAmount(newTotal);
																						}
																					}
																				}}
																				onBlur={() => {
																					setTimeout(() => {
																						calculateRowTotals();
																						calculateTotal();
																						handleApplyAdvanceAmount();
																					}, 10);
																				}}
																			/>
																		</Form.Item>
																	</TableCell>

																	{screen !== 'VIEW' && (
																		<TableCell align="center">
																			<>
																				{fields.length === 1 && index === 0 && (
																					<Tooltip title="Add Row">
																						<AddIconButton
																							onClick={() => handleAdvanceAmountAddRow(index)}
																						/>
																					</Tooltip>
																				)}

																				{fields.length > 1 && index > 0 && index < fields.length - 1 && (
																					<Tooltip title="Delete Row">
																						<DeleteIconButton
																							onClick={() => {
																								const currentValues = [
																									...form.getFieldValue('advanceAmountList'),
																								];

																								const newRowTotals = [...rowTotals];
																								newRowTotals.splice(index, 1);
																								setRowTotals(newRowTotals);

																								currentValues.splice(index, 1);

																								for (let i = index; i < currentValues.length; i++) {
																									if (i > 0) {
																										const prevTo = parseInt(
																											currentValues[i - 1].to
																										);
																										if (!isNaN(prevTo)) {
																											currentValues[i].from = prevTo + 1;
																										}
																									}
																								}

																								form.setFieldValue(
																									'advanceAmountList',
																									currentValues
																								);

																								const newTotal = newRowTotals.reduce(
																									(total, rowTotal) => total + (rowTotal || 0),
																									0
																								);
																								setTotalAmount(newTotal);
																								handleApplyAdvanceAmount();
																							}}
																						/>
																					</Tooltip>
																				)}

																				{fields.length > 1 && index === fields.length - 1 && (
																					<>
																						<Tooltip title="Add Row">
																							<AddIconButton
																								onClick={() => handleAdvanceAmountAddRow(index)}
																							/>
																						</Tooltip>

																						<Tooltip title="Delete Row">
																							<DeleteIconButton
																								onClick={() => {
																									const currentValues = [
																										...form.getFieldValue('advanceAmountList'),
																									];

																									const newRowTotals = [...rowTotals];
																									newRowTotals.splice(index, 1);
																									setRowTotals(newRowTotals);

																									currentValues.splice(index, 1);

																									for (
																										let i = index;
																										i < currentValues.length;
																										i++
																									) {
																										if (i > 0) {
																											const prevTo = parseInt(
																												currentValues[i - 1].to
																											);
																											if (!isNaN(prevTo)) {
																												currentValues[i].from = prevTo + 1;
																											}
																										}
																									}

																									form.setFieldValue(
																										'advanceAmountList',
																										currentValues
																									);

																									const newTotal = newRowTotals.reduce(
																										(total, rowTotal) => total + (rowTotal || 0),
																										0
																									);
																									setTotalAmount(newTotal);
																									handleApplyAdvanceAmount();
																								}}
																							/>
																						</Tooltip>
																					</>
																				)}

																				{fields.length > 1 && index === 0 && <span></span>}
																			</>
																		</TableCell>
																	)}
																</TableRow>
															))
														) : (
															<TableRow>
																<TableCell colSpan={5} style={{ textAlign: 'center' }}>
																	No data. Click the plus icon to add new entries.
																</TableCell>
															</TableRow>
														)}
													</TableBody>

													<TableFooter>
														<TableRow sx={{ height: 24 }}>
															<TableCell
																colSpan={2}
																align="right"
																sx={{ fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f6f5f5', pr: '14px' }}
															>
																Advance Payment
															</TableCell>

															<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>
																<NumericFormat
																	value={totalAmount || 0}
																	displayType="input"
																	decimalScale={2}
																	fixedDecimalScale
																	thousandSeparator="," style={{fontWeight: 'bold',
																		backgroundColor: undefined,
																		borderColor: undefined}}
																	customInput={Input}
																	disabled={true}
																	readOnly={true}
																/>
															</TableCell>
															{screen !== 'VIEW' && <TableCell colSpan={1}></TableCell>}
														</TableRow>
													</TableFooter>
												</Table>
											</TableContainer>

											<Form.Item name="advanceAmounts" noStyle />
										</>
									);
								}}
							</Form.List>
						</div>

						<Form.Item name="advanceAmountListSet" noStyle />
					</SectionCard>
				</Col>
			</Row>

			<SectionCard sx={detailSectionCardSx}>
				<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
					Other Payments & Incentive
				</Typography>

				<Row gutter={[16, 0]}>
					<Col xs={24} md={6}>
						<Form.Item
							name={constantFormValues?.upfrontLeasePayment?.key}
							rules={constantFormValues?.upfrontLeasePayment?.rules}
							label={constantFormValues?.upfrontLeasePayment?.label}
						>
							<NumericFormat
								decimalScale={2}
								placeholder="0.00"
								fixedDecimalScale
								customInput={Input}
								thousandSeparator=","
								isAllowed={withValueCap}
								disabled={screen === 'VIEW' ? true : false}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={6}>
						<Form.Item
							name={constantFormValues?.initialDirectCost?.key}
							rules={constantFormValues?.initialDirectCost?.rules}
							label={constantFormValues?.initialDirectCost?.label}
						>
							<NumericFormat
								decimalScale={2}
								placeholder="0.00"
								fixedDecimalScale
								customInput={Input}
								thousandSeparator=","
								isAllowed={withValueCap}
								disabled={screen === 'VIEW' ? true : false}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={6}>
						<Form.Item
							name={constantFormValues?.securityDeposit?.key}
							rules={constantFormValues?.securityDeposit?.rules}
							label={constantFormValues?.securityDeposit?.label}
						>
							<NumericFormat
								decimalScale={2}
								placeholder="0.00"
								fixedDecimalScale
								customInput={Input}
								thousandSeparator=","
								isAllowed={withValueCap}
								disabled={screen === 'VIEW' ? true : false}
							/>
						</Form.Item>
					</Col>

					<Col xs={24} md={6}>
						<Form.Item
							name={constantFormValues?.cashIncentiveAmount?.key}
							rules={constantFormValues?.securityDeposit?.rules}
							label={constantFormValues?.cashIncentiveAmount?.label}
						>
							<NumericFormat
								decimalScale={2}
								placeholder="0.00"
								fixedDecimalScale
								customInput={Input}
								thousandSeparator=","
								isAllowed={withValueCap}
								disabled={screen === 'VIEW' ? true : false}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Form.Item name="grossRents" noStyle />
				<Form.Item name="advanceAmounts" noStyle />
				<Form.Item name={constantFormValues?.advanceAmount?.key} noStyle />
				<Form.Item noStyle name={constantFormValues?.inputChangeMonth?.key} />
			</SectionCard>

			<SectionCard sx={detailSectionCardSx}>
				<Typography variant="h4" sx={{ ...sectionTitleSx, marginBottom: '16px' }}>
					IFRS - 16 Specific Fields
				</Typography>

				<Row gutter={[16, 16]}>
					<Col xs={24}>
						<Form.List name="changes">
							{(fields) => (
								<TableContainer
									sx={{
										width: '100%',
										display: 'block',
										overflowX: 'auto',
										position: 'relative',

										'& table, & th, & td': {
											border: '1px solid #e0e0e0',
											borderCollapse: 'collapse',
										},
										'& th, & td': {
											p: '5px',
										},
										'& td .ant-form-item': {
											marginBottom: '0px',
										},

										'& td:not(:last-child)': { borderRight: '1px solid #e0e0e0' },
										'& tr:not(:last-child)': { borderBottom: '1px solid #e0e0e0' },
										'& th': { borderBottom: '1px solid #e0e0e0' },
									}}
								>
									<Table
										sx={{
											'& .MuiTableBody-root .MuiTableCell-root': { px: '5px', py: '5px' },
											tableLayout: 'fixed',
										}}
									>
										<TableHead>
											<TableRow>
												<Form.Item noStyle shouldUpdate>
													{({ getFieldValue }) => {
														const leaseType = getFieldValue('leaseType');

														return (
															<>
																{constantFormValues?.changes?.orderedRowHeader.map((key) => {
																	if (key === 'inputChangeDiscountRate' && leaseType === 'Operating Lease')
																		return null;

																	return (
																		<TableCell
																			key={constantFormValues?.changes?.changeList[key].key}
																			sx={{ fontSize: '13px', backgroundColor: '#f6f5f5' }}
																			align="center"
																		>
																			{constantFormValues?.changes?.changeList[key].label}
																		</TableCell>
																	);
																})}
															</>
														);
													}}
												</Form.Item>
											</TableRow>
										</TableHead>

										<TableBody>
											{fields?.map((field, index) => {
												const maxDate = getMaxDateForRow(index);

												return (
													<TableRow key={index}>
														<TableCell style={{ position: 'relative' }}>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].key,
																]}
																rules={constantFormValues?.changes?.changeList['inputChangeEffectiveDate'].rules}
															>
																<DatePicker
																	disabled={index === 0 ? true : false}
																	onChange={(date, dateString) => {
																		if (date === null || date === undefined || dateString === '') {
																			handleInputMonthChange(null, index);
																		} else {
																			handleInputMonthChange(date, index);
																		}
																	}}
																	format="YYYY-MM-DD" style={{width: '100%'}}
																	className="antd-datePicker"
																	disabledDate={(current) => {
																		if (!current) {
																			return false;
																		}

																		const leaseStartDateValue = form.getFieldValue('leaseStartDate');

																		if (!leaseStartDateValue) {
																			return false;
																		}

																		const leaseStartDate = dayjs(leaseStartDateValue);

																		if (!leaseStartDate.isValid()) {
																			return false;
																		}

																		let minimumAllowedDate = leaseStartDate;

																		if (maxRecordedDate?.minimumDateLimit) {
																			minimumAllowedDate = dayjs(maxRecordedDate.minimumDateLimit)
																				.add(1, 'month')
																				.startOf('month');
																		}
																		const isBeforeMinimum = current.isBefore(minimumAllowedDate, 'day');

																		let isAfterMaximum = false;
																		if (maxDate) {
																			isAfterMaximum = current.isAfter(dayjs(maxDate), 'day');
																		}

																		const requiredDayOfMonth = leaseStartDate.date();
																		const isWrongDayOfMonth = current.date() !== requiredDayOfMonth;

																		return isAfterMaximum || isWrongDayOfMonth;
																	}}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeDescription'].key,
																]}
															>
																<Select
																	disabled
																	showSearch
																	filterOption={true} style={{textAlign: 'center'}}
																	options={
																		constantFormValues?.changes?.changeList['inputChangeDescription'].options
																	}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[
																	field.name,
																	constantFormValues?.changes?.changeList['inputChangeLeaseTerm'].key,
																]}
															>
																<Input
																	disabled
																	type="number" style={{marginBottom: 0,
																		textAlign: 'center',
																		color:
																			index !== 0
																				? form.getFieldValue(['changes', index, 'inputChangeLeaseTerm']) ===
																					form.getFieldValue(['changes', index - 1, 'inputChangeLeaseTerm'])
																					? 'black'
																					: 'blue'
																				: 'black'}}
																/>
															</Form.Item>
														</TableCell>

														<Form.Item name={[field.name, 'inputChangeDiscountRate']} noStyle />

														<Form.Item noStyle shouldUpdate={(prev, current) => prev.leaseType !== current.leaseType}>
															{({ getFieldValue }) =>
																getFieldValue('leaseType') === 'Finance Lease' ? (
																	<TableCell align="center">
																		<Form.Item
																			style={{ margin: 0 }}
																			name={[
																				field.name,
																				constantFormValues?.changes?.changeList['inputChangeDiscountRate']
																					.key,
																			]}
																		>
																			<NumericFormat
																				decimalScale={2}
																				placeholder="0.00"
																				fixedDecimalScale
																				thousandSeparator=","
																				isAllowed={withValueCap} style={{textAlign: 'center',
																					color:
																						index !== 0
																							? form.getFieldValue([
																									'changes',
																									index,
																									'inputChangeDiscountRate',
																								]) ===
																								form.getFieldValue([
																									'changes',
																									index - 1,
																									'inputChangeDiscountRate',
																								])
																								? 'black'
																								: 'blue'
																							: 'black'}}
																				customInput={Input}
																				disabled={true}
																			/>
																		</Form.Item>
																	</TableCell>
																) : null
															}
														</Form.Item>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentFrequency'].key]}
															>
																<Select
																	disabled
																	showSearch style={{textAlign: 'center'}}
																	filterOption={true}
																	options={constantFormValues?.changes?.changeList['paymentFrequency'].options}
																/>
															</Form.Item>
														</TableCell>

														<TableCell>
															<Form.Item
																style={{ margin: 0 }}
																name={[field.name, constantFormValues?.changes?.changeList['paymentTime'].key]}
															>
																<Select
																	disabled
																	showSearch
																	filterOption={true} style={{textAlign: 'center'}}
																	options={constantFormValues?.changes?.changeList['paymentTime'].options}
																/>
															</Form.Item>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</Form.List>
					</Col>
				</Row>
			</SectionCard>

			{showFooterActions && (
				<>
					<Box
						sx={{
							gap: '4px',
							pt: 0,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
						}}
					>
						<Box
							sx={{
								gap: '4px',
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
							}}
						>
							{screen === 'CREATE' ? (
								<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
									<Button
										type="primary"
										onClick={handleSubmit}
										style={{
											backgroundColor: theme.palette.lynTheme.main,
										}}
									>
										Submit
									</Button>
								</Box>
							) : (
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										gap: '8px',
										width: '100%',
									}}
								>
									<Box>
										<Button
											type="primary"
											onClick={() => setShowGoAndSetSubmitModal(true)}
											style={{
												backgroundColor: theme.palette.lynTheme.main,
											}}
										>
											Go & Set
										</Button>
									</Box>

									<Box sx={{ display: 'flex', gap: '6px' }}>
										<Button
											style={{
												display: 'flex',
												fontSize: '13px',
												fontWeight: '500',
												borderRadius: '4px',
												alignItems: 'center',
											}}
											icon={<img src={editIcon} width={16} height={16} alt="edit" />}
											onClick={(e) => {
												e.stopPropagation();

												if (navigate && state) {
													navigate('/static/lease-test-bed-v9/edit', {
														state: state,
													});
												}
											}}
										>
											Modify
										</Button>
									</Box>
								</Box>
							)}
						</Box>
					</Box>

					{showGoAndSetSubmitModal && (
						<ComptrollerConfirmModal
							showModal={showGoAndSetSubmitModal}
							handleSubmit={handleGoAndSetButton}
							setShowModal={setShowGoAndSetSubmitModal}
							modalData={{
								head: state?.processName ?? '',
								label: 'Do you want to proceed?',
							}}
						/>
					)}
				</>
			)}
		</Box>
	);
};

export default LeaseV9NewTrailModeDetailsForm;